import { useEffect, useMemo, useState } from 'react';
import { extractApiError } from '@/lib/api-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  useCreateMappingTemplate,
  useDryRun,
  useLiveRun,
  useImportRun,
  isImportRunFinished,
  useMappingTemplates,
  useUploadCsv,
  useUploadPreview,
  type FieldMap,
  type FieldMapEntry,
  type RunResult,
} from '@/hooks/use-import-export';
import { useT } from '@/i18n/use-t';

// `key` is the canonical field identifier the importer matches on — never
// translated. Only `label` is user-facing; `labelKey` is absent for `SKU`,
// which stays verbatim in every locale.
const CANONICAL_FIELDS: {
  key: string;
  label: string;
  labelKey?: string;
  required: boolean;
  /**
   * RL-812: one of a group of fields is required — the importer refuses a row
   * with neither `categoryId` nor `categoryName`.
   */
  oneOf?: 'category';
  /**
   * RL-1038: a line under the label. The two category fields carry one, so the
   * one-of rule is read on the screen and not only in the mark's hover title.
   */
  hint?: { key: string; text: string };
}[] = [
  { key: 'sku', label: 'SKU', required: true },
  { key: 'name', label: 'Name', labelKey: 'importExport.csvImport.fieldName', required: true },
  {
    key: 'description',
    label: 'Description',
    labelKey: 'importExport.csvImport.fieldDescription',
    required: false,
  },
  {
    key: 'categoryId',
    label: 'Category ID',
    labelKey: 'importExport.csvImport.fieldCategoryId',
    required: false,
    oneOf: 'category',
    hint: {
      key: 'importExport.csvImport.categoryIdHint',
      text: "An existing category's ID. To use the names in your file, map Category name instead.",
    },
  },
  {
    key: 'categoryName',
    label: 'Category name (find or create)',
    labelKey: 'importExport.csvImport.fieldCategoryName',
    required: false,
    oneOf: 'category',
    hint: {
      key: 'importExport.csvImport.categoryNameHint',
      text: 'Finds the category by name, or creates it. Map this or Category ID.',
    },
  },
  {
    key: 'taxCategory',
    label: 'Tax category',
    labelKey: 'importExport.csvImport.fieldTaxCategory',
    required: true,
  },
  {
    key: 'primaryBarcode',
    label: 'Primary barcode',
    labelKey: 'importExport.csvImport.fieldPrimaryBarcode',
    required: false,
  },
  {
    key: 'isMarked',
    label: 'Excise stamp (true/false)',
    labelKey: 'importExport.csvImport.fieldIsMarked',
    required: false,
  },
  {
    key: 'exciseStampCount',
    label: 'Excise stamps per unit',
    labelKey: 'importExport.csvImport.fieldExciseStampCount',
    required: false,
  },
  // SSC-108: grams with up to 3 decimals (`.` or `,`); blank keeps the stored weight.
  {
    key: 'grossWeightGrams',
    label: 'Gross weight, g',
    labelKey: 'importExport.csvImport.fieldGrossWeight',
    required: false,
  },
  {
    key: 'netWeightGrams',
    label: 'Net weight, g',
    labelKey: 'importExport.csvImport.fieldNetWeight',
    required: false,
  },
  // SSC-109: `presence`, `tolerance` or `disabled`; blank keeps the stored mode.
  {
    key: 'weightCheckMode',
    label: 'Weight check mode',
    labelKey: 'importExport.csvImport.fieldWeightCheckMode',
    required: false,
  },
];

export function CsvImportPage() {
  const t = useT('admin_ui');
  const upload = useUploadCsv();
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [suggested, setSuggested] = useState<string | null>(null);

  const preview = useUploadPreview(uploadId);
  const templates = useMappingTemplates({ sourceKind: 'csv', targetKind: 'catalog_item' });
  const createTemplate = useCreateMappingTemplate();
  const dryRun = useDryRun();
  const liveRun = useLiveRun();

  const [chosenTemplateId, setChosenTemplateId] = useState<string>('');
  const [mapping, setMapping] = useState<FieldMap>({});
  const [constantValues, setConstantValues] = useState<Record<string, string>>({
    taxCategory: 'standard',
  });
  const [templateName, setTemplateName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dryResult, setDryResult] = useState<RunResult | null>(null);
  // RL-825: the live run is queued; it is followed by its id until it finishes.
  const [liveRunId, setLiveRunId] = useState<string | null>(null);
  const liveRunLookup = useImportRun(liveRunId);
  const liveRunView = liveRunLookup.data;

  // RL-365: the suggestion is applied ONCE per upload. `pendingSuggestion`
  // is cleared as soon as it is applied — or as soon as the operator edits
  // the mapping themselves — so a later templates refetch (e.g. after
  // "Save template") can never overwrite a hand-built mapping.
  const [pendingSuggestion, setPendingSuggestion] = useState<string | null>(null);

  // When the suggested template arrives, prefill mapping from it.
  useEffect(() => {
    if (pendingSuggestion === null) return;
    const tpl = templates.data?.find((t) => t.id === pendingSuggestion);
    if (tpl === undefined) return;
    applyTemplate(tpl.id);
    setPendingSuggestion(null);
  }, [pendingSuggestion, templates.data]);

  function applyTemplate(id: string) {
    const tpl = templates.data?.find((t) => t.id === id);
    if (tpl === undefined) return;
    setChosenTemplateId(id);
    setMapping(tpl.fieldMap);
    const constants: Record<string, string> = {};
    for (const [k, v] of Object.entries(tpl.fieldMap)) {
      if (v.by === 'constant') constants[k] = v.value;
    }
    setConstantValues((prev) => ({ ...prev, ...constants }));
  }

  function setEntry(target: string, entry: FieldMapEntry | null) {
    setPendingSuggestion(null);
    setMapping((prev) => {
      const next = { ...prev };
      if (entry === null) delete next[target];
      else next[target] = entry;
      return next;
    });
  }

  const dirtyMapping = useMemo(() => mapping, [mapping]);
  const canRun = uploadId !== null && Object.keys(dirtyMapping).length > 0;
  // RL-812: what the importer would refuse on every row, named before the run.
  const missingRequired = missingRequiredFields(mapping).map((field) =>
    field === 'category'
      ? t('importExport.csvImport.categoryRequiredLabel', 'Category (ID or name)')
      : (() => {
          const f = CANONICAL_FIELDS.find((c) => c.key === field)!;
          return f.labelKey === undefined ? f.label : t(f.labelKey, f.label);
        })(),
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">
          {t('importExport.csvImport.title', 'CSV import')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'importExport.csvImport.intro',
            'Upload a CSV → match columns to product fields → dry-run → commit. Save the mapping as a template to skip the matching step on the next file with the same shape.',
          )}
        </p>
      </header>

      {/* Step 1 — upload */}
      <section className="rounded-md border bg-background p-5">
        <h2 className="mb-3 text-sm font-medium">
          {t('importExport.csvImport.step1', '1 · Upload file')}
        </h2>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file === undefined) return;
            setError(null);
            try {
              const res = await upload.mutateAsync(file);
              setUploadId(res.uploadId);
              setHeaders(res.headers);
              setSuggested(res.suggestedTemplateId);
              setPendingSuggestion(res.suggestedTemplateId);
              setMapping({});
              setChosenTemplateId('');
              setDryResult(null);
              setLiveRunId(null);
            } catch (err) {
              setError(extractError(err));
            }
          }}
        />
        {uploadId !== null && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('importExport.csvImport.uploaded', 'Uploaded · {count} rows previewed').replace(
              '{count}',
              String(preview.data?.rows.length ?? '…'),
            )}
            {suggested !== null &&
              ` · ${t('importExport.csvImport.autoTemplate', 'auto-suggested template applied')}`}
          </p>
        )}
      </section>

      {/* Step 2 — choose / build mapping */}
      {uploadId !== null && (
        <section className="rounded-md border bg-background p-5 space-y-4">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-medium">
              {t('importExport.csvImport.step2', '2 · Field mapping')}
            </h2>
            <div className="flex items-center gap-2">
              <Label className="text-xs">
                {t('importExport.csvImport.useTemplate', 'Use saved template:')}
              </Label>
              <Select
                className="w-64"
                value={chosenTemplateId}
                onChange={(e) => {
                  setPendingSuggestion(null);
                  if (e.target.value !== '') applyTemplate(e.target.value);
                  else {
                    setChosenTemplateId('');
                    setMapping({});
                  }
                }}
              >
                <option value="">
                  {t('importExport.csvImport.manualMapping', '— manual mapping —')}
                </option>
                {templates.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          </header>

          <Table>
            <THead>
              <TR>
                <TH>{t('importExport.csvImport.colField', 'Canonical field')}</TH>
                <TH>{t('importExport.csvImport.colSource', 'Source')}</TH>
                <TH>{t('importExport.csvImport.colFrom', 'From')}</TH>
              </TR>
            </THead>
            <TBody>
              {CANONICAL_FIELDS.map((f) => {
                const entry = mapping[f.key];
                const mode = entry?.by ?? 'unset';
                return (
                  <TR key={f.key}>
                    <TD>
                      {f.labelKey === undefined ? f.label : t(f.labelKey, f.label)}
                      {f.required && <span className="ml-1 text-destructive">*</span>}
                      {f.oneOf === 'category' && (
                        <span
                          className="ml-1 text-amber-600"
                          title={t(
                            'importExport.csvImport.categoryOneOf',
                            'Map Category ID or Category name',
                          )}
                        >
                          †
                        </span>
                      )}
                      {f.hint !== undefined && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t(f.hint.key, f.hint.text)}
                        </p>
                      )}
                    </TD>
                    <TD>
                      <Select
                        className="w-40"
                        value={mode}
                        onChange={(e) => {
                          const v = e.target.value as 'unset' | 'column' | 'constant';
                          if (v === 'unset') setEntry(f.key, null);
                          else if (v === 'column') {
                            setEntry(f.key, { by: 'column', value: headers[0] ?? '' });
                          } else {
                            setEntry(f.key, { by: 'constant', value: constantValues[f.key] ?? '' });
                          }
                        }}
                      >
                        <option value="unset">
                          {t('importExport.csvImport.optUnset', '— unset —')}
                        </option>
                        <option value="column">
                          {t('importExport.csvImport.optColumn', 'column')}
                        </option>
                        <option value="constant">
                          {t('importExport.csvImport.optConstant', 'constant')}
                        </option>
                      </Select>
                    </TD>
                    <TD>
                      {entry?.by === 'column' && (
                        <Select
                          className="w-64"
                          value={entry.value}
                          onChange={(e) => setEntry(f.key, { by: 'column', value: e.target.value })}
                        >
                          {headers.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </Select>
                      )}
                      {entry?.by === 'constant' && (
                        <Input
                          className="w-64"
                          value={entry.value}
                          onChange={(e) => {
                            setEntry(f.key, { by: 'constant', value: e.target.value });
                            setConstantValues((prev) => ({ ...prev, [f.key]: e.target.value }));
                          }}
                        />
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>

          <p className="text-xs text-muted-foreground">
            {t(
              'importExport.csvImport.requiredLegend',
              '* required · † one of Category ID or Category name is required',
            )}
          </p>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[280px]">
              <Label className="text-xs">
                {t('importExport.csvImport.saveAsTemplate', 'Save as template (optional)')}
              </Label>
              <Input
                placeholder={t(
                  'importExport.csvImport.templateNamePlaceholder',
                  'e.g. Acme weekly price file',
                )}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              disabled={templateName.trim() === '' || createTemplate.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await createTemplate.mutateAsync({
                    name: templateName.trim(),
                    sourceKind: 'csv',
                    sourceConfig: { delimiter: ',', hasHeaderRow: true },
                    targetKind: 'catalog_item',
                    fieldMap: mapping,
                    matchHeaders: headers,
                  });
                  setTemplateName('');
                } catch (err) {
                  setError(extractError(err));
                }
              }}
            >
              {t('importExport.csvImport.saveTemplate', 'Save template')}
            </Button>
          </div>
        </section>
      )}

      {/* Step 3 — dry-run */}
      {canRun && (
        <section className="rounded-md border bg-background p-5 space-y-3">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-medium">
              {t('importExport.csvImport.step3', '3 · Dry-run')}
            </h2>
            <Button
              disabled={dryRun.isPending || missingRequired.length > 0}
              onClick={async () => {
                setError(null);
                setDryResult(null);
                try {
                  const r = await dryRun.mutateAsync({
                    uploadId: uploadId!,
                    fieldMap: mapping,
                    targetKind: 'catalog_item',
                  });
                  setDryResult(r);
                } catch (err) {
                  setError(extractError(err));
                }
              }}
            >
              {t('importExport.csvImport.runDryRun', 'Run dry-run')}
            </Button>
          </header>
          {missingRequired.length > 0 && (
            <p className="text-sm text-destructive">
              {t(
                'importExport.csvImport.missingRequired',
                'Map these before the dry-run: {fields}',
              ).replace('{fields}', missingRequired.join(', '))}
            </p>
          )}
          {dryResult !== null && (
            <RunSummary
              result={dryResult}
              label={t('importExport.csvImport.labelDryRun', 'dry-run')}
            />
          )}
        </section>
      )}

      {/* Step 4 — live run */}
      {dryResult !== null && (
        <section className="rounded-md border bg-background p-5 space-y-3">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-medium">
              {t('importExport.csvImport.step4', '4 · Commit')}
            </h2>
            <Button
              disabled={
                liveRun.isPending ||
                dryResult.totals.rows === 0 ||
                (liveRunId !== null && !isImportRunFinished(liveRunView?.status))
              }
              onClick={async () => {
                setError(null);
                setLiveRunId(null);
                try {
                  const started = await liveRun.mutateAsync({
                    uploadId: uploadId!,
                    fieldMap: mapping,
                    targetKind: 'catalog_item',
                  });
                  setLiveRunId(started.runId);
                } catch (err) {
                  setError(extractError(err));
                }
              }}
            >
              {t('importExport.csvImport.runLive', 'Run live import')}
            </Button>
          </header>
          {liveRunId !== null && liveRunLookup.isError && (
            // The run is accepted and may still be running: the live import
            // stays fenced and only the same run's status is checked again.
            <div className="flex items-center gap-3">
              <p className="text-sm text-destructive">
                {t(
                  'importExport.csvImport.liveStatusUnavailable',
                  "Could not read the import's status. It may still be running, so do not start it again.",
                )}
              </p>
              <Button
                size="sm"
                variant="outline"
                disabled={liveRunLookup.isFetching}
                onClick={() => {
                  void liveRunLookup.refetch();
                }}
              >
                {t('importExport.csvImport.liveStatusRetry', 'Check status again')}
              </Button>
            </div>
          )}
          {liveRunId !== null &&
            !(liveRunLookup.isError && liveRunView === undefined) &&
            (liveRunView === undefined || liveRunView.status === 'pending' ? (
              <p className="text-sm text-muted-foreground">
                {t('importExport.csvImport.liveQueued', 'Import queued — waiting to start…')}
              </p>
            ) : liveRunView.status === 'running' ? (
              <p className="text-sm text-muted-foreground">
                {t('importExport.csvImport.liveRunning', 'Importing… {ok} of {rows} rows so far')
                  .replace('{ok}', String(liveRunView.totals.ok))
                  .replace('{rows}', String(liveRunView.totals.rows))}
              </p>
            ) : (
              <>
                {liveRunView.status === 'failed' && (
                  <p className="text-sm text-destructive">
                    {t(
                      'importExport.csvImport.liveFailed',
                      'The import failed. See the totals and row errors below.',
                    )}
                  </p>
                )}
                <RunSummary
                  result={{ runId: liveRunId, ...liveRunView }}
                  label={t('importExport.csvImport.labelLive', 'live')}
                />
              </>
            ))}
        </section>
      )}

      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/**
 * RL-812: the fields the importer requires on every row that this mapping
 * leaves unmapped — each required field, and `category` when neither
 * Category ID nor Category name is mapped. A column or a non-blank constant
 * counts as mapped.
 */
function missingRequiredFields(mapping: FieldMap): string[] {
  const mapped = (key: string) => {
    const entry = mapping[key];
    return entry !== undefined && entry.value.trim() !== '';
  };
  const missing = CANONICAL_FIELDS.filter((f) => f.required && !mapped(f.key)).map((f) => f.key);
  const categoryKeys = CANONICAL_FIELDS.filter((f) => f.oneOf === 'category').map((f) => f.key);
  if (!categoryKeys.some(mapped)) missing.push('category');
  return missing;
}

function RunSummary({ result, label }: { result: RunResult; label: string }) {
  const t = useT('admin_ui');
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <Badge variant="success">
          {result.totals.ok} {t('importExport.csvImport.summaryOk', 'ok')}
        </Badge>
        <Badge variant={result.totals.errors === 0 ? 'outline' : 'destructive'}>
          {result.totals.errors} {t('importExport.csvImport.summaryErrors', 'errors')}
        </Badge>
        <span className="text-muted-foreground">
          {result.totals.rows} {t('importExport.csvImport.summaryRows', 'rows')} · {label}
        </span>
      </div>
      {result.errors.length > 0 && (
        <div className="rounded-md border bg-background">
          <Table>
            <THead>
              <TR>
                <TH className="w-16">{t('importExport.csvImport.colRow', 'Row')}</TH>
                <TH>{t('importExport.csvImport.colError', 'Error')}</TH>
                <TH>{t('importExport.csvImport.colSource', 'Source')}</TH>
              </TR>
            </THead>
            <TBody>
              {result.errors.slice(0, 50).map((e) => (
                <TR key={e.rowIndex}>
                  <TD className="font-mono text-xs">{e.rowIndex}</TD>
                  <TD className="text-sm text-destructive">{e.error}</TD>
                  <TD className="font-mono text-xs text-muted-foreground">
                    {JSON.stringify(e.sourceRow).slice(0, 120)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// RFL-203: shared extractor — surfaces backend issues + a 5xx status so a
// storage/backend failure reads clearly instead of "Internal server error".
const extractError = extractApiError;
